<script setup lang="ts">
import merge from 'lodash-es/merge';
import TilesRow from './TilesRow.vue';

defineOptions({
    name: 'WidgetTilesSettings'
});

const { $t } = useLocalization();
const { $alert } = useAlerts();

const emit = defineEmits<{
    (e: 'update:modelValue', payload: any): void;
}>();

const props = defineProps<{
    modelValue: any;
}>();

const defaults = ref<any>({
    title: '',
    subtitle: '',
    bgColor: '#ffffff',
    description: '',
    rows: []
});

const form = computed<FormType<any>>(() =>
{
    return props.modelValue;
});

function triggetModel(): void
{
    emit('update:modelValue', form.value);
}

function addRow(): void
{
    const ids = form.value.settings.rows.map((x: { id: any }) => x.id);

    const maxId = form.value.settings.rows.length > 0 ? Math.max(...ids) : 1;

    const lastIndex = form.value.settings.rows.length - 1;

    if (form.value.settings.rows[lastIndex].tiles.length < 3)
    {
        $alert.warning($t('[[[Nie wypełniono poprzedniego wiersza.]]]'));
    }
    else
    {
        form.value.settings.rows.push({
            id: maxId + 1,
            tiles: []
        });
    }
}

function removeRow(): void
{
    form.value.settings.rows.pop();
}

watch(() => form.value.settings, (value: any) =>
{
    triggetModel();
}, { deep: true });

onBeforeMount(async (): Promise<void> =>
{
    form.value.settings = merge(defaults.value, form.value.settings);

    if (!form.value.settings.rows.length)
    {
        form.value.settings.rows = [{
            id: 1,
            tiles: []
        }];
    }
});
</script>

<template>
    <div class="widget-tiles">
        <div class="widget-tiles__settings">
            <IdeoFormGroup :label="$t('[[[Tytuł]]]')" :invalid-feedback="form.$errors.first('settings.title')" :state="form.$errors.state('settings.title')">
                <IdeoFormInput v-model="form.settings.title" type="text" name="settings.title" />
            </IdeoFormGroup>
            <IdeoFormGroup :label="$t('[[[Podtytuł]]]')" :invalid-feedback="form.$errors.first('settings.subtitle')" :state="form.$errors.state('settings.subtitle')">
                <IdeoFormInput v-model="form.settings.subtitle" type="text" name="subtitle" />
            </IdeoFormGroup>
            <IdeoFormGroup :label="$t('[[[Kolor tła]]]')" :invalid-feedback="form.$errors.first('settings.bgColor')" :state="form.$errors.state('settings.bgColor')" required>
                <IdeoFormInput v-model="form.settings.bgColor" type="color" name="settings.bgColor" />
            </IdeoFormGroup>
        </div>
        <div v-for="(row, id) in form.settings.rows" :key="row.id" class="widget-tiles-rows">
            <TilesRow :id="id" v-model="form" :origin-id="id" />
        </div>
        <div class="widget-tiles widget-tiles__buttons">
            <ActionButton variant="light" class="w-100" icon="far fa-plus" :text="$t('[[[Dodaj wiersz]]]')" @click="addRow()" />
            <div class="input-group-append">
                <ActionButton
                    variant="danger"
                    class="w-100"
                    icon="far fa-trash"
                    :text="$t('[[[Usuń wiersz]]]')"
                    :title="$t('[[[Usuń obrazek]]]')"
                    :confirm="$t('[[[Potwierdzenie usunięcia]]]')"
                    :disabled="form.settings.rows.length < 2"
                    @click="removeRow()"
                />
            </div>
        </div>
        <IdeoFormGroup :label="$t('[[[Opis]]]')" :invalid-feedback="form.$errors.first('settings.description')" :state="form.$errors.state('settings.description')">
            <RichTextEditor id="description" v-model="form.settings.description" name="settings.description" />
        </IdeoFormGroup>
    </div>
</template>

<style lang="scss" scoped>
.widget-tiles {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.widget-tiles__settings {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 20px;

    @media only screen and (min-width: 960px)
    {
        grid-template-columns: 1fr 1fr;
    }
}
</style>
